/**
 * เลือก y ของจุดเกิด/จุดเข้าฉากให้ตัวละคร **เดินต่อได้จริง**
 *
 * แยกออกมาจาก `PlayerScene` เพื่อเทสต์กับผัง `.tmj` ของจริงได้โดยไม่ต้องมี Phaser
 * ดูเหตุผลเต็มที่ `PlayerScene.freeSpotY`
 */

export type SolidGrid = {
  /** จำนวนช่องแนวนอน/แนวตั้ง */
  cols: number;
  rows: number;
  tileW: number;
  tileH: number;
  /** true = ทะลุไม่ได้ */
  solid: (col: number, row: number) => boolean;
};

export type SpawnOpts = {
  /** ตัวชน [กว้าง, สูง] */
  collider: readonly [number, number];
  /** ฉาก top-down ต้องการ "แถวที่ทะลุซ้าย-ขวาได้" ไม่ใช่แค่ช่องว่าง */
  requireLane: boolean;
  sceneH: number;
};

const tilesOverlapping = (
  g: SolidGrid, x0: number, y0: number, w: number, h: number,
): boolean => {
  const c0 = Math.floor(x0 / g.tileW);
  const c1 = Math.floor((x0 + w - 1) / g.tileW);
  const r0 = Math.floor(y0 / g.tileH);
  const r1 = Math.floor((y0 + h - 1) / g.tileH);
  for (let r = Math.max(0, r0); r <= Math.min(g.rows - 1, r1); r += 1) {
    for (let c = Math.max(0, c0); c <= Math.min(g.cols - 1, c1); c += 1) {
      if (g.solid(c, r)) return true;
    }
  }
  return false;
};

/** แถวทั้งหมดที่ตัวชนกินอยู่ ต้องว่างตลอดความกว้างของผัง */
export function laneOpen(g: SolidGrid, bottom: number, ch: number): boolean {
  const r0 = Math.floor((bottom - ch) / g.tileH);
  const r1 = Math.floor((bottom - 1) / g.tileH);
  if (r0 < 0 || r1 >= g.rows) return false;
  for (let r = r0; r <= r1; r += 1) {
    for (let c = 0; c < g.cols; c += 1) {
      if (g.solid(c, r)) return false;
    }
  }
  return true;
}

/** ยืนตรงนี้ได้ไหม (ไม่จมกำแพง + ถ้าเป็น top-down ต้องเดินทะลุได้ด้วย) */
export function canEnterAt(
  g: SolidGrid, x: number, bottom: number, o: SpawnOpts,
): boolean {
  const [cw, ch] = o.collider;
  if (bottom - ch < 0 || bottom > o.sceneH) return false;
  if (tilesOverlapping(g, x - cw / 2, bottom - ch, cw, ch)) return false;
  return !o.requireLane || laneOpen(g, bottom, ch);
}

/**
 * หา y ที่ใกล้ `wantY` ที่สุดแล้วเข้าได้จริง — คืน `null` เมื่อหาไม่เจอ
 * ผู้เรียกต้องตัดสินใจเองว่าจะใช้ค่าเดิมต่อหรือแจ้ง error
 */
export function findEntryY(
  g: SolidGrid, x: number, wantY: number, o: SpawnOpts,
): number | null {
  if (canEnterAt(g, x, wantY, o)) return wantY;
  const STEP = 10;
  for (let i = 1; i <= 120; i += 1) {
    for (const cand of [wantY + i * STEP, wantY - i * STEP]) {
      if (canEnterAt(g, x, cand, o)) return cand;
    }
  }
  return null;
}
